import { useMemo, useState } from 'react';

import { observer } from 'mobx-react-lite';

import { Button } from '@components/button';
import { Spinner } from '@components/loading-spinner';
import { SelectOption } from '@components/selectmulti';
import { useToaster } from '@components/toaster';

import {
  useBulkGrantAccessToApplicants,
  useBulkRemoveApplicationAccess,
  useUserApplicationAccessForJob,
} from 'api/jobs/hooks';
import { UserApplicationAccessForJob } from 'api/jobs/requests';
import { ApplicationSearch } from 'components/ApplicationSearch';
import { Modal, ModalProps } from 'features/modals';
import { useStore } from 'models/Provider';

function EditApplicationAccessModal({
  jobId,
  userId,
  userName,
  closeModal,
}: ModalProps<{
  jobId: number;
  userId: number;
  userName: string;
}>) {
  const { t } = useStore();
  const { showSuccess, showError } = useToaster();
  const applicationAccessQuery = useUserApplicationAccessForJob(jobId, userId);
  const original: SelectOption[] = useMemo(() => {
    return (applicationAccessQuery?.data ?? []).map((application: UserApplicationAccessForJob) => ({
      value: application.id.toString(),
      label: application.name,
    }));
  }, [applicationAccessQuery]);

  const [selection, setSelection] = useState<SelectOption[] | undefined>(undefined);
  const selected: SelectOption[] = useMemo(() => {
    return selection ?? original ?? [];
  }, [selection, original]);
  const isLoaded = applicationAccessQuery.isSuccess;

  const selectedValues = selected.map((a) => a.value);
  const originalValues = original.map((o) => o.value);
  const addedApplicationIds = selectedValues.filter((a) => !originalValues.includes(a));
  const removedApplicationIds = originalValues.filter((a) => !selectedValues.includes(a));

  const onError = () => {
    showError(t('job.recruiters.editaccess.error', userName));
  };
  const onAddSuccess = async () => {
    closeModal();
    showSuccess(t('job.recruiters.editaccess.success', userName));
  };
  const bulkGrantAccessMutation = useBulkGrantAccessToApplicants({
    onError: onError,
    onSuccess: onAddSuccess,
  });
  const addAccess = async () => {
    await bulkGrantAccessMutation.mutateAsync({
      jobId: jobId,
      applications: addedApplicationIds,
      users: [userId],
    });
  };
  const bulkRemoveAccessMutation = useBulkRemoveApplicationAccess({
    onError: onError,
    onSuccess: addAccess,
  });

  const saving = bulkGrantAccessMutation.isLoading || bulkRemoveAccessMutation.isLoading;

  const handleSave = async () => {
    await bulkRemoveAccessMutation.mutateAsync({
      jobId: jobId,
      applications: removedApplicationIds,
      users: [userId],
    });
  };

  const isSame = useMemo(() => {
    if (selected.length !== original.length) return false;
    return selected.every((a) => original.includes(a));
  }, [selected, original]);

  return (
    <Modal>
      <Modal.Header text={t('job.recruiters.editaccess.heading', userName)} />
      {saving && <Spinner />}
      <ApplicationSearch
        key={`${isLoaded}`}
        /* @ts-ignore ApplicationSearch is untyped */
        onSelect={setSelection}
        jobId={jobId}
        selected={selected}
        label={t('job.access.editaccess.addapplicant')}
      />
      <Modal.Actions>
        <Button
          variant="secondary"
          onClick={closeModal}
          isDisabled={saving}
          text={t('job.recruiters.editaccess.button.cancel')}
        />
        <Button
          variant="primary-blue"
          onClick={handleSave}
          isDisabled={isSame || saving}
          text={t('job.recruiters.editaccess.button.submit')}
        />
      </Modal.Actions>
    </Modal>
  );
}

export default observer(EditApplicationAccessModal);
